import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { HelpIcon } from "@/components/ui/help-icon";
import { useState, useCallback } from "react";
import { useGenerateConfig, useReloadConfig } from "@/hooks/use-config";
import { toast } from "sonner";
import {
  Cable,
  Shield,
  Radio,
  Fingerprint,
  Layers,
  PhoneCall,
  Webhook,
  Globe,
  Wand2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileCode,
  KeyRound,
  Terminal,
  ScrollText,
  ChevronLeft,
  LayoutDashboard,
  Activity,
  FileText,
  Network,
} from "lucide-react";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  exact?: boolean;
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
}

const nav: NavGroup[] = [
  {
    label: null,
    items: [
      { label: "Overview", path: "/", icon: <LayoutDashboard size={16} />, exact: true },
    ],
  },
  {
    label: "Calls",
    items: [
      { label: "Active Calls", path: "/calls/active", icon: <Activity size={16} /> },
      { label: "Call History", path: "/calls/history", icon: <FileText size={16} /> },
    ],
  },
  {
    label: "Configuration",
    items: [
      { label: "Trunk Contexts", path: "/config/contexts", icon: <Layers size={16} /> },
      { label: "Transports", path: "/config/transports", icon: <Cable size={16} /> },
      { label: "Auths", path: "/config/auths", icon: <Shield size={16} /> },
      { label: "AORs", path: "/config/aors", icon: <Radio size={16} /> },
      { label: "Identifies", path: "/config/identifies", icon: <Fingerprint size={16} /> },
      { label: "Extensions", path: "/config/extensions", icon: <PhoneCall size={16} /> },
      { label: "ARI Apps", path: "/config/ari-apps", icon: <Webhook size={16} /> },
      { label: "WebSocket", path: "/config/websocket-endpoints", icon: <Globe size={16} /> },
      { label: "HEP / Homer", path: "/config/hep", icon: <Network size={16} /> },
    ],
  },
  {
    label: "Administration",
    items: [
      { label: "AMI Console", path: "/admin/ami-console", icon: <Terminal size={16} /> },
      { label: "Server Logs", path: "/admin/logs", icon: <ScrollText size={16} /> },
      { label: "Generated Config", path: "/admin/config", icon: <FileCode size={16} /> },
      { label: "MCP API Keys", path: "/admin/api-keys", icon: <KeyRound size={16} /> },
      { label: "Audit Logs", path: "/admin/audit-logs", icon: <ClipboardList size={16} /> },
    ],
  },
];

export function Sidebar() {
  const location = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("voicing-sidebar") === "collapsed"
  );
  const generate = useGenerateConfig();
  const reload = useReloadConfig();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("voicing-sidebar", next ? "collapsed" : "expanded");
      return next;
    });
  }, []);

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const handleGenerate = () => {
    generate.mutate("all", {
      onSuccess: (data) => toast.success(`Config generated: ${Object.keys(data.results).join(", ")}`),
      onError: () => toast.error("Config generation failed"),
    });
  };

  const handleReload = () => {
    reload.mutate("all", {
      onSuccess: () => toast.success("Config reloaded"),
      onError: () => toast.error("Config reload failed"),
    });
  };

  const isItemActive = (item: NavItem) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path);

  return (
    <aside
      className={cn(
        "group/sidebar relative z-10 flex h-full shrink-0 flex-col border-r border-border bg-sidebar transition-[width] duration-200 ease-out",
        collapsed ? "w-14" : "w-56"
      )}
    >
      <button
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-3 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-base opacity-0 transition-[opacity,color,border-color] duration-150 ease-out hover:border-border-hover hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 group-hover/sidebar:opacity-100"
      >
        <ChevronLeft
          size={13}
          className={cn(
            "transition-transform duration-200",
            collapsed && "rotate-180"
          )}
        />
      </button>
      <nav
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-thin",
          collapsed ? "px-2" : "px-3"
        )}
      >
        {nav.map((group, groupIndex) => {
          const isGroupCollapsed =
            !collapsed && group.label !== null && collapsedGroups.has(group.label);
          return (
            <div key={group.label ?? "root"} className={cn(groupIndex > 0 && "mt-4")}>
              {collapsed ? (
                groupIndex > 0 && <div className="mx-1 mb-3 border-t border-border" />
              ) : (
                group.label !== null && (
                  <button
                    onClick={() => toggleGroup(group.label!)}
                    className="mb-1 flex w-full cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground transition-colors duration-150 ease-out hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
                  >
                    {isGroupCollapsed ? (
                      <ChevronRight size={11} />
                    ) : (
                      <ChevronDown size={11} />
                    )}
                    {group.label}
                  </button>
                )
              )}
              {!isGroupCollapsed && (
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "flex items-center rounded-sm text-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30",
                        collapsed
                          ? "h-9 w-9 justify-center mx-auto"
                          : "gap-2.5 px-2.5 py-1.5",
                        isItemActive(item)
                          ? "bg-accent text-foreground font-medium"
                          : "text-sidebar-foreground hover:text-foreground hover:bg-accent/60"
                      )}
                    >
                      <span className="shrink-0">{item.icon}</span>
                      {!collapsed && (
                        <span className="truncate whitespace-nowrap">
                          {item.label}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <Separator />
      {/* Saving already regenerates and reloads the module you edited, so these
          two buttons do nothing extra in normal editing. Without saying so the
          only honest reading of "Generate All Config" is that saves do NOT
          apply on their own, which is exactly backwards. */}
      <div className={cn("space-y-2 py-4", collapsed ? "px-2" : "px-3")}>
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerate}
          loading={generate.isPending}
          className={cn("h-8 text-xs gap-1.5", collapsed ? "w-9 px-0" : "w-full")}
          title="Rewrite every .conf file from the database. Saving already does this for the section you edited — click this only to force a resync, e.g. on a fresh deploy or after a reload that could not reach Asterisk."
        >
          <Wand2 size={14} />
          {!collapsed && "Generate All Config"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleReload}
          loading={reload.isPending}
          className={cn("h-8 text-xs gap-1.5", collapsed ? "w-9 px-0" : "w-full")}
          title="Make Asterisk re-read every .conf file on disk. Saving already reloads the affected module — click this only if a change looks saved but is not live."
        >
          <RefreshCw size={14} />
          {!collapsed && "Reload All Config"}
        </Button>
        {!collapsed && (
          <p className="px-0.5 pt-0.5 text-[11px] leading-relaxed text-muted-foreground">
            Changes apply automatically when you save — use these only to force a
            resync if something looks out of date.
            <HelpIcon text="Every save rewrites the config file for the section you edited and reloads that module in Asterisk before it reports success, so you never need to generate or reload by hand after an edit. Reach for these if Asterisk was unreachable when you saved, on a fresh deployment before any config exists, or if the drift warning is showing — the banner's own Regenerate & reload is the more targeted fix there." />
          </p>
        )}
      </div>

    </aside>
  );
}
